import $ from 'jquery';
import moment from 'moment-timezone';
import { EventEmitter } from 'events';
import { isEqual, find, get, truncate } from 'lodash';
import classnames from 'classnames';
import { canUserUseBlockTime, canUserBookAppointments } from './users';
import { t } from '../../../modules/infra/i18n';
import {
  isLastRecurring,
  isCardOrReaderPayment,
  isGiftCardPayment,
  isBookedViaSquire,
  isBookedViaPhoneAgent,
  isBookedViaChatbotAgent
} from './appointments';
import config from '../../../config';
import { APPOINTMENT_STATUS } from '../../main/appointments/constants';
import squireLogo from '../../../../images/squire_circle_logo.png';
import phoneAgent from '../../../../images/phone-agent.svg';
import chatbotAgent from '../../../../images/chatbot-agent.svg';
import manOffImage from '../../../../images/man-off.png';
import manImage from '../../../../images/man.png';
import recurrImage from '../../../../images/three-circling-arrows.png';
import lastRecurrImage from '../../../../images/end-recurring.png';
import notesImage from '../../../../images/notes-image.png';
import failedPayment from '../../../../images/failed-payment.png';
import randomImage from '../../../../images/random.png';
import loyaltyReward from '../../../../images/loyalty-reward-icon.svg';
import creditCard from '../../../../images/credit-card.svg';
import giftCard from '../../../../images/gift-card-icon.svg';
import waiverWarning from '../../../../images/exclamation_circle_red.png';
import membershipSign from '../../../../images/membership-letter.png';
import blockedCustomerIcon from '../../../../images/block-client.svg';
import groupAppointment from '../../../../images/group-appointment.svg';
import { isCustomerBlockedFromBooking } from '../../main/customers/customerForm/helpers';

export const canShowAppointmentOnCalendar = appointment => appointment.duration > 0;

// TODO: Rewrite after implementing normalizr https://github.com/paularmstrong/normalizr
export function prepareAppointment(appointment, timezone, minInterval, user) {
  const startTime = moment(appointment.start).tz(timezone);
  const endTime = moment(appointment.end).tz(timezone);
  const todayStart = moment.utc().tz(timezone).startOf('day');
  const now = moment.utc().tz(timezone).add(minInterval, 'mins');
  const isPast = endTime < now;
  const isTodayAndPast = startTime >= todayStart && isPast;

  const ar = appointment;
  ar.start = startTime.format();
  ar.end = endTime.format();

  ar.className = classnames({
    'is-canceled-no-show': ar.status === APPOINTMENT_STATUS.canceled && ar.noShow,
    'is-canceled': ar.status === APPOINTMENT_STATUS.canceled,
    'is-no-show': ar.noShow,
    'is-paid': appointment.paymentStatus === 'paid',
    'is-past': isTodayAndPast,
    'is-refunded': !!appointment.refundedAmount,
    'u-opacity': isPast,
  });

  // change color this is new customer
  if (appointment.isNewShopCustomer || appointment.isNewBarberCustomer) {
    ar.barberColor = '#0071f7';
  }

  if (appointment.retainedBarberCustomer) {
    ar.barberColor = '#670893';
  }

  ar.timezone = timezone;
  if (!ar.paymentType) {
    ar.paymentType = get(ar, 'paymentTypes[0]', null);
  }

  ar.isBlocked = isCustomerBlockedFromBooking({
    userKind: user.kind,
    customer: appointment.customer,
    barberId: appointment.barberId,
    shopId: appointment.shopId,
    brandId: appointment.shop.brandId,
  });

  return ar;
}

export function prepareBlockTime(dayOff, timezone, barber = {}) {
  const data = {};

  const end = moment.tz(dayOff.end, timezone);

  data.className = classnames({
    'u-opacity': end.isBefore(new Date()),
  });

  data.start = moment
    .tz(dayOff.start, timezone)
    .format();
  data.end = end.format();
  data.rendering = 'background';
  data.description = truncate(dayOff.description, { length: 30 });
  data.blockTime = true;
  data.dayOff = {
    ...dayOff,
    barberId: barber.id,
  };

  return data;
}

// TODO: Use shallow compare https://facebook.github.io/react/docs/shallow-compare.html
// after implementing normalizr https://github.com/paularmstrong/normalizr
export function isAppointmentsChanged(oldAppointments, newAppointments, timezone, minInterval, user) {
  const prepareCalendarAppointment = appointment => prepareAppointment(appointment, timezone, minInterval, user);
  let oldAppointmentsArray = [];
  if (oldAppointments && oldAppointments.length) {
    oldAppointmentsArray = oldAppointments.map(prepareCalendarAppointment);
  }
  let newAppointmentsArray = [];
  if (newAppointments && newAppointments.length) {
    newAppointmentsArray = newAppointments.map(prepareCalendarAppointment);
  }
  return !isEqual(oldAppointmentsArray, newAppointmentsArray);
}

const BLOCK_TIME_CLASS = 'js-blocktime';

const img = (icon, shape, tip, modifiers, automationId) => {
  const classNames = classnames('c-calendar-icon', `c-calendar-icon--${shape}`, modifiers);
  const automationIdAttribute = automationId ? `data-automation-id="${automationId}"` : '';
  return `<img class="${classNames}" src="${icon}" title="${tip}" ${automationIdAttribute} />`;
};

export function eventRender(event, element) {
  if (event.status !== APPOINTMENT_STATUS.canceled) {
    element.find('.fc-content').append('<div class="fc-event__icons" />');
    const iconsElement = element.find('.fc-event__icons');

    if (event.noShow) {
      iconsElement.append(
        img(
          manOffImage,
          'circle',
          t('appointment:calendar.tooltips.customer_no_show'),
          undefined,
          `icon:appt:${event.id}:clientNoShow`
        )
      );
    } else if (event.status === APPOINTMENT_STATUS.arrived) {
      iconsElement.append(
        img(
          manImage,
          'circle',
          t('appointment:calendar.tooltips.customer_arrived'),
          undefined,
          `icon:appt:${event.id}:clientArrived`
        )
      );
    }

    if (event.isRecurring && isLastRecurring(event.start, moment(event.recurrUntil))) {
      iconsElement.append(
        img(
          lastRecurrImage,
          'circle',
          t('appointment:calendar.tooltips.last_repeat'),
          undefined,
          `icon:appt:${event.id}:lastRepeat`
        )
      );
    } else if (event.isRecurring) {
      iconsElement.append(
        img(
          recurrImage,
          'circle',
          t('appointment:calendar.tooltips.repeat_appointment'),
          undefined,
          `icon:appt:${event.id}:repeat`
        )
      );
    }

    if (isBookedViaSquire(event) && event.paymentStatus !== 'failed') {
      iconsElement.append(
        img(
          squireLogo,
          'circle',
          t('appointment:calendar.tooltips.paid_with_squire'),
          undefined,
          `icon:appt:${event.id}:paidSquire`
        )
      );
    }

    if (isBookedViaPhoneAgent(event)) {
      iconsElement.append(
        img(
          phoneAgent,
          'transparent',
          'Paid with Phone Agent',
          undefined,
          `icon:appt:${event.id}:byPhoneAgent`
        )
      );
    }

    if (isBookedViaChatbotAgent(event)) {
      iconsElement.append(
        img(
          chatbotAgent,
          'transparent',
          'Paid with Chatbot Agent',
          undefined,
          `icon:appt:${event.id}:byChatbotAgent`
        )
      );
    }

    if (event.paymentStatus === 'failed') {
      iconsElement.append(
        img(
          failedPayment,
          'circle',
          t('appointment:calendar.tooltips.payment_failed'),
          undefined,
          `icon:appt:${event.id}:onlinePayFail`
        )
      );
    }

    if (isCardOrReaderPayment(event)) {
      iconsElement
        .append(img(
          creditCard,
          'square',
          t('appointment:calendar.tooltips.paid_with_card'),
          'c-calendar-icon--transparent',
          `icon:appt:${event.id}:paidCard`
        ));
    }

    if (event.isRewardPromo) {
      iconsElement.append(
        img(
          loyaltyReward,
          'circle',
          t('appointment:calendar.tooltips.loyalty_reward'),
          undefined,
          `icon:appt:${event.id}:loyaltyReward`
        )
      );
    }

    if (event.groupId) {
      iconsElement.append(
        img(
          groupAppointment,
          'square',
          t('appointment:calendar.tooltips.group_appointment'),
          'c-calendar-icon--groupAppt',
          `icon:appt:${event.id}:groupAppointment`
        )
      );
    }

    if (event.customerNotes || get(event, 'customer.notes')) {
      iconsElement.append(
        img(
          notesImage,
          'square',
          t('appointment:calendar.tooltips.has_notes'),
          undefined,
          `icon:appt:${event.id}:hasNotes`
        )
      );
    }

    if (event.bookedWithAnyBarber) {
      iconsElement.append(
        img(
          randomImage,
          'circle',
          t('appointment:calendar.tooltips.any_barber'),
          undefined,
          `icon:appt:${event.id}:anyBarber`
        )
      );
    }

    if (event.giftCardId && isGiftCardPayment(event)) {
      iconsElement.append(
        img(
          giftCard,
          'transparent',
          t('appointment:calendar.tooltips.gift_card_paid'),
          undefined,
          `icon:appt:${event.id}:paidGiftCard`
        )
      );
    }

    if (get(event, 'shop.requiresWaiver') && event.waiver === false) {
      iconsElement.append(
        img(
          waiverWarning,
          'circle',
          t('appointment:calendar.tooltips.waiver_not_filled'),
          undefined,
          `icon:appt:${event.id}:noWaiver`
        )
      );
    }

    if (event.bookedWithMembership) {
      element.find('.fc-time').append(img(
        membershipSign,
        'circle',
        t('appointment:calendar.tooltips.membership_booking'),
        undefined,
        `icon:appt:${event.id}:membership`
      ));
    }
  }

  const diff = moment(event.end).diff(moment(event.start), 'minutes'); // in minutes
  if (diff > 15) {
    const description = `<div class="fc-event__description">${event.description}</div>`;
    element.find('.fc-content').append(description);
  }

  const borderColor = `4px solid ${event.barberColor}`;
  element.css('border-left', borderColor);

  if (event.rendering === 'background' && event.blockTime) {
    element.data('dayOff', event.dayOff);
    element.addClass(`c-blocktime ${BLOCK_TIME_CLASS}`);
    element.append(event.description);
    element.attr('data-automation-id', `container:blockedTime:${event.dayOff?.id}`);
  }
}

export function eventAfterRender(event, element) {
  if (!event.blockTime) {
    const eventHeightPx = element[0].offsetHeight;
    const eventWidthPx = element[0].offsetWidth;

    // set grid layout for different event sizes
    if (eventHeightPx < 35) {
      // 1 line of text
      element.find('.fc-content').addClass('fc-content__1-line');
      element.find('.fc-event__description').addClass('fc-event-section__hidden');

      if (eventWidthPx < 110) {
        element.find('.fc-time').addClass('fc-event-section__hidden');
        element.find('.fc-event__icons').addClass('fc-event-section__hidden');
      } else if (eventWidthPx < 180) {
        element.find('.fc-time span').text(element.find('.fc-time').data('start'));
        element.find('.fc-event__icons').addClass('fc-event-section__hidden');
      } else if (eventWidthPx < 250) {
        element.find('.fc-event__icons').addClass('fc-event__icons_short');
      }
    } else if (eventHeightPx < 55) {
      // 2 lines of text
      if (eventWidthPx < 110) {
        element.find('.fc-content').addClass('fc-content__1-column');
        element.find('.fc-event__description').addClass('fc-event-section__hidden');
        element.find('.fc-event__icons').addClass('fc-event-section__hidden');
      } else if (eventWidthPx < 180) {
        element.find('.fc-content').addClass('fc-content__2-lines-compact');
        element.find('.fc-event__icons').addClass('fc-event-section__hidden');
        element.find('.fc-time span').text(element.find('.fc-time').data('start'));
      }
    } else if (eventHeightPx < 75) {
      // 3 lines of text
      if (eventWidthPx < 180) {
        element.find('.fc-content').addClass('fc-content__1-column');
        element.find('.fc-event__icons').addClass('fc-event-section__hidden');
      }
    } else if (eventWidthPx < 180) {
      element.find('.fc-content').addClass('fc-content__1-column');
    }

    // expand service name to 2 lines when possible
    if ((eventWidthPx < 180 && eventHeightPx > 80)
      || (eventWidthPx > 180 && eventHeightPx > 55)) {
      element.find('.fc-event__description').addClass('fc-event-section__2-lines');
    }

    // expand customer name to full name when possible
    if (eventWidthPx > 230 || eventHeightPx > 100) {
      element.find('.fc-title').text(event.customerFullName);
    }

    const titleText = element.find('.fc-title').text();
    element.find('.fc-title').empty();
    element.find('.fc-title').append(`<div class="fc-title__text">${titleText}</div>`);
    if (event.isBlocked) {
      element.find('.fc-title').append(
        img(
          blockedCustomerIcon,
          'circle',
          t('appointment:calendar.tooltips.blocked_client'),
          'fc-blocked-customer-icon',
          `icon:appt:${event.id}:blockedClient`
        ),
      );
    }

    if (eventHeightPx > 100) {
      element.find('.fc-title__text').addClass('fc-event-section__2-lines');
    }

    element.find('.fc-content').addClass('fc-content_with-block');

    // allow time string to wrap to second line when possible
    if (eventWidthPx < 111 && eventHeightPx > 120) {
      element.find('.fc-time').addClass('fc-event-section__2-lines');
    }
    element.find('.fc-content').attr('data-automation-id', `container:appt:${event.id}`);
    element.find('.fc-title__text').attr('data-automation-id', `text:appt:${event.id}:clientName`);
    element.find('.fc-time span').attr('data-automation-id', `text:appt:${event.id}:time`);
    element.find('.fc-event__description').attr('data-automation-id', `text:appt:${event.id}:description`);
  }
}

export function eventClick(event, appointmentsArray, permissions, fetchAppointment) {
  if (!event.start) return false;

  const callback = (appointment) => {
    const data = { ...appointment };
    data.start = moment.utc(appointment.start).toJSON();
    data.end = moment.utc(appointment.end).toJSON();
    EventEmitter.prototype.emit('appointment-clicked', data);
    EventEmitter.prototype.emit('open-appointment-modal', 'details');
    return true;
  };

  if (fetchAppointment) {
    return fetchAppointment(event.id).then(callback);
  }

  const currentAppointment = find(appointmentsArray, { id: event.id });
  return callback(currentAppointment);
}

export const openAppointmentFormWithDateTime = ({ barberId, day, time }) => {
  EventEmitter.prototype.emit('appointment-created', { barberId, day, time });
  EventEmitter.prototype.emit('open-appointment-modal', 'new');
};

export function dayClick(date, jsEvent, view, barberId = null, permissions) {
  if (!canUserBookAppointments(permissions)) return false;
  if (get(jsEvent, 'target.className', '').includes(BLOCK_TIME_CLASS)) {
    $(jsEvent.target).data('date', date);
    return false;
  }

  // Disable appointment form on small screens
  if (window.screen.availWidth < 415) {
    return false;
  }

  const data = {
    barberId,
    day: date.format(config.dateFormatISO),
    time: (view.type !== 'month' ? date.format('HH:mm') : null),
  };
  return openAppointmentFormWithDateTime(data);
}

export const selectTime = (start, end, jsEvent, view, barber, hideBarber, permissions) => {
  if (!canUserUseBlockTime(permissions)) return;
  EventEmitter.prototype.emit('open-block-time-modal', {
    barberId: barber.id,
    start,
    end,
    hideBarber,
  });
};
